import { useEffect, useState } from "react";
import { api, inr, fmtDate, formatApiError } from "@/lib/api";
import { DatePicker } from "@/components/DatePicker";
import PageHeader from "@/components/PageHeader";
import { usePager, Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, CheckCircle2, XCircle, BookOpen, AlertTriangle, FileText, Upload, Sparkles, Paperclip, PackageX, ReceiptText, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const GST_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
function validGSTIN(g) {
  g = (g || "").trim().toUpperCase();
  if (!GSTIN_RE.test(g)) return false;
  let total = 0;
  for (let i = 0; i < 14; i++) {
    const p = GST_CHARS.indexOf(g[i]) * (i % 2 ? 2 : 1);
    total += Math.floor(p / 36) + (p % 36);
  }
  return GST_CHARS[(36 - (total % 36)) % 36] === g[14];
}

const empty = { name: "", gstin: "", contact_person: "", phone: "", email: "", address: "", address2: "", state: "", notes: "", credit_days: 60 };

export default function Vendors() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [ledgerVendor, setLedgerVendor] = useState(null);
  const emptyBill = { number: "", date: "", amount: "", po_number: "", notes: "", is_cash: false };
  const [bill, setBill] = useState(emptyBill);
  const [billFile, setBillFile] = useState(null);
  const [attachingBill, setAttachingBill] = useState("");
  const attachBillFile = async (b, f) => {
    if (!f) return;
    setAttachingBill(b.id);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/vendors/bills/${b.id}/attach-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Bill file attached to ${b.number || "bill"}`);
      refreshLedger();
    } catch (e) { toast.error(formatApiError(e)); }
    setAttachingBill("");
  };
  const viewBillFile = async (fid) => {
    try {
      const r = await api.get(`/files/${fid}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Could not open bill file"); }
  };
  const [aiPay, setAiPay] = useState(null);
  const [aiPayBusy, setAiPayBusy] = useState(false);

  const uploadPayProof = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !ledgerVendor) return;
    setAiPayBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/vendors/${ledgerVendor.id}/payments/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAiPay({ ...r.data, date: r.data.payment_date || "" });
      toast.success(r.data.matched_bills?.length ? `AI matched ${r.data.matched_bills.length} bill(s)` : "Proof uploaded — review the details");
    } catch (err) { toast.error(formatApiError(err)); }
    setAiPayBusy(false);
  };

  const saveAiPay = async () => {
    if (!(+aiPay.amount > 0)) { toast.error("Enter a valid amount"); return; }
    const billNos = (aiPay.matched_bills || []).map((b) => b.bill_number);
    try {
      await api.post(`/vendors/${ledgerVendor.id}/payments`, {
        amount: +aiPay.amount, date: aiPay.date || null, mode: aiPay.mode || "Bank Transfer",
        reference: aiPay.reference || "", po_number: "",
        notes: billNos.length ? `Against bill(s) ${billNos.join(", ")}` : "",
        file_id: aiPay.file_id, bill_numbers: billNos,
      });
      toast.success("Payment recorded");
      setAiPay(null); refreshLedger();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [dnFor, setDnFor] = useState(null);
  const dnPos = dnFor?.bill
    ? ((dnFor.bill.allocations || []).filter((a) => a.po_number).length
        ? dnFor.bill.allocations.filter((a) => a.po_number)
        : (dnFor.bill.po_number ? [{ po_number: dnFor.bill.po_number, amount: +dnFor.bill.amount || 0 }] : []))
    : [];
  const dnShare = dnPos.find((a) => a.po_number === dnFor?.po_number)?.amount;
  const issueDn = async () => {
    if (!(+dnFor.amount > 0)) { toast.error("Enter a valid debit amount"); return; }
    if (dnPos.length > 1 && !dnFor.po_number) { toast.error("This bill covers multiple POs — select which PO the rejected material belongs to"); return; }
    try {
      const r = await api.post(`/vendors/${ledgerVendor.id}/bills/${dnFor.bill.id}/debit-note`,
        { amount: +dnFor.amount, reason: dnFor.reason, action: dnFor.action || "",
          dn_number: (dnFor.dn_number || "").trim(), po_number: dnFor.po_number || "", date: dnFor.date || "" });
      const aff = [...(r.data.affected?.pos || []), ...(r.data.affected?.orders || [])];
      toast.success(`Debit Note issued${aff.length ? " — " + aff.join("; ") : ""}`);
      if (r.data.email_sent) toast.success(`Debit Note emailed to ${r.data.email_to} with branded PDF attached`);
      else toast.warning("Debit Note email not sent — add the vendor's email to send it automatically");
      if (dnFor.action === "cancel") toast.info("If the customer already paid, record the refund under Payments → Record Payment → Refund");
      setDnFor(null); refreshLedger();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const viewDnPdf = async (bid) => {
    try {
      const r = await api.get(`/vendors/bills/${bid}/debit-note-pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Could not open Debit Note"); }
  };
  const removeDn = async (b) => {
    if (!window.confirm(`Remove Debit Note ${b.debit_note_number} from bill ${b.number}? You can then issue a fresh one with the correct number.`)) return;
    try {
      await api.delete(`/vendors/${ledgerVendor.id}/bills/${b.id}/debit-note`);
      toast.success(`Debit Note ${b.debit_note_number} removed — issue a fresh one with the correct number`);
      refreshLedger();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const emptyPay = { amount: "", date: "", mode: "Bank Transfer", reference: "", bill_number: "", notes: "" };
  const [vpay, setVpay] = useState(emptyPay);

  const openLedger = async (v) => {
    try {
      const r = await api.get(`/vendors/${v.id}/ledger`);
      setLedger(r.data); setLedgerVendor(v); setBill(emptyBill); setVpay(emptyPay); setPayBills({}); setBillFile(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const refreshLedger = async () => {
    const r = await api.get(`/vendors/${ledgerVendor.id}/ledger`);
    setLedger(r.data);
  };
  const [club, setClub] = useState(false);
  const [clubRows, setClubRows] = useState([{ po_number: "", amount: "" }, { po_number: "", amount: "" }]);
  const clubSum = +clubRows.reduce((s, r) => s + (+r.amount || 0), 0).toFixed(2);
  const clubSel = clubRows.filter((r) => r.po_number);
  const clubOk = clubSel.length >= 2 && Math.abs(clubSum - (+bill.amount || 0)) <= 0.01;

  const resplit = (rows, totalStr) => {
    const total = +totalStr || 0;
    const sel = rows.filter((r) => r.po_number);
    if (!sel.length || total <= 0) return rows;
    const totals = Object.fromEntries((ledger?.purchase_orders || []).map((p) => [p.number, +p.total || 0]));
    const base = sel.reduce((s, r) => s + (totals[r.po_number] || 0), 0);
    let acc = 0;
    const amounts = sel.map((r, i) => {
      if (i === sel.length - 1) return +(total - acc).toFixed(2);
      const a = +(total * (base > 0 ? (totals[r.po_number] || 0) / base : 1 / sel.length)).toFixed(2);
      acc += a;
      return a;
    });
    let j = 0;
    return rows.map((r) => (r.po_number ? { ...r, amount: String(amounts[j++]) } : r));
  };
  const setClubPo = (i, v) => setClubRows((rows) => resplit(rows.map((r, idx) => (idx === i ? { ...r, po_number: v } : r)), bill.amount));
  const setClubAmt = (i, v) => setClubRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, amount: v } : r)));
  const removeClubRow = (i) => setClubRows((rows) => resplit(rows.filter((_, idx) => idx !== i), bill.amount));
  const resetClub = () => { setClub(false); setClubRows([{ po_number: "", amount: "" }, { po_number: "", amount: "" }]); };

  const addBill = async () => {
    if (!bill.is_cash && !bill.number.trim()) { toast.error("Bill number is required (or tick Cash purchase)"); return; }
    if (!(+bill.amount > 0)) { toast.error("Enter a valid amount"); return; }
    if (club) {
      if (clubSel.length < 2) { toast.error("Select at least 2 POs to club — or switch back to Single PO"); return; }
      if (clubSel.some((r) => !(+r.amount > 0))) { toast.error("Enter a split amount for every clubbed PO"); return; }
      if (Math.abs(clubSum - +bill.amount) > 0.01) { toast.error(`Split total ${inr(clubSum)} must equal the bill amount ${inr(+bill.amount)}`); return; }
    }
    try {
      const body = { ...bill, amount: +bill.amount };
      if (club) { body.po_number = ""; body.allocations = clubSel.map((r) => ({ po_number: r.po_number, amount: +r.amount })); }
      if (body.date) body.date = new Date(body.date).toISOString(); else delete body.date;
      const resp = await api.post(`/vendors/${ledgerVendor.id}/bills`, body);
      if (billFile && resp.data?.id) {
        try {
          const fd = new FormData();
          fd.append("file", billFile);
          await api.post(`/vendors/bills/${resp.data.id}/attach-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch { toast.warning("Bill saved, but the file upload failed — attach it from Payables → bill row"); }
      }
      if (club) {
        toast.success(`Bill recorded — clubbed across ${clubSel.length} POs: ${clubSel.map((r) => `${r.po_number} (${inr(+r.amount)})`).join(", ")}`, { duration: 8000 });
      } else if (selPo && liveExcess > 1) {
        toast.warning(`Excess billing! Bills against ${selPo.number} now exceed the PO value by ${inr(liveExcess)}`, { duration: 8000 });
      } else {
        toast.success(bill.is_cash ? "Cash purchase (no invoice) recorded" : "Vendor bill recorded");
      }
      setBill(emptyBill); setBillFile(null); resetClub(); refreshLedger();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delBill = async (bid) => {
    if (!window.confirm("Delete this bill?")) return;
    try { await api.delete(`/vendors/bills/${bid}`); refreshLedger(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const [payBills, setPayBills] = useState({});
  const openBills = (ledger?.bills || []).filter((b) => b.number && (b.due_amount || 0) > 0.01);
  const selPayBills = openBills.filter((b) => payBills[b.number]);
  const togglePayBill = (b) => {
    const n = { ...payBills, [b.number]: !payBills[b.number] };
    setPayBills(n);
    const sum = openBills.filter((x) => n[x.number]).reduce((t, x) => t + (x.due_amount || 0), 0);
    setVpay((v) => ({ ...v, amount: sum > 0 ? String(+sum.toFixed(2)) : "" }));
  };

  const addPayment = async () => {
    if (!(+vpay.amount > 0)) { toast.error("Enter a valid payment amount"); return; }
    try {
      const body = { amount: +vpay.amount, date: vpay.date, mode: vpay.mode, reference: vpay.reference,
                     notes: vpay.notes || "", bill_numbers: selPayBills.map((b) => b.number) };
      if (body.date) body.date = new Date(body.date).toISOString(); else delete body.date;
      const r = await api.post(`/vendors/${ledgerVendor.id}/payments`, body);
      const allocs = r.data.allocations || [];
      if (allocs.length) {
        const parts = allocs.map((a) => `${a.bill_number || "bill"} ${a.cleared ? "cleared" : "part-paid"} (${inr(a.amount)})`);
        toast.success(`Payment ${inr(+vpay.amount)} recorded — oldest bills first: ${parts.join(", ")}${r.data.unallocated > 0 ? ` · ${inr(r.data.unallocated)} kept on account (advance)` : ""}`, { duration: 10000 });
      } else {
        toast.success(`Payment of ${inr(+vpay.amount)} recorded for ${ledgerVendor.name}${r.data.unallocated > 0 ? " — no open bills, kept on account" : ""}`);
      }
      setVpay(emptyPay); setPayBills({}); refreshLedger();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delPayment = async (pid) => {
    if (!window.confirm("Delete this payment?")) return;
    try { await api.delete(`/vendors/payments/${pid}`); refreshLedger(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const openStatement = async () => {
    try {
      const r = await api.get(`/vendors/${ledgerVendor.id}/ledger/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const billedByPo = {};
  (ledger?.bills || []).forEach((b) => {
    const allocs = (b.allocations && b.allocations.length)
      ? b.allocations
      : (b.po_number ? [{ po_number: b.po_number, amount: +b.amount || 0 }] : []);
    const allocSum = allocs.reduce((s, a) => s + (+a.amount || 0), 0);
    const dn = +b.debit_amount || 0;
    allocs.forEach((a) => {
      const amt = +a.amount || 0;
      const net = amt - (allocSum > 0 && dn > 0 ? dn * (amt / allocSum) : 0);
      billedByPo[a.po_number] = (billedByPo[a.po_number] || 0) + net;
    });
  });
  const excessPos = (ledger?.purchase_orders || []).filter((p) => (billedByPo[p.number] || 0) > (+p.total || 0) + 1);
  const poStatus = (p) => {
    const billed = billedByPo[p.number] || 0;
    const total = +p.total || 0;
    if (billed > total + 1) return { label: `Excess +${inr(billed - total)}`, cls: "bg-rose-100 text-rose-700" };
    if (total > 0 && billed >= total - 1) return { label: "Fully Billed", cls: "bg-emerald-100 text-emerald-700" };
    if (billed > 0) return { label: "Partially Billed", cls: "bg-amber-100 text-amber-700" };
    return { label: "Unbilled", cls: "bg-slate-100 text-slate-600" };
  };
  const selPo = (ledger?.purchase_orders || []).find((p) => p.number === bill.po_number);
  const liveExcess = selPo ? (billedByPo[selPo.number] || 0) + (+bill.amount || 0) - (+selPo.total || 0) : 0;

  const load = () => api.get("/vendors").then((r) => setList(r.data));
  const pager = usePager(list);
  const vlPoPager = usePager(ledger?.purchase_orders || []);
  const vlBillPager = usePager(ledger?.bills || []);
  useEffect(() => { load(); }, []);

  const gstStatus = form.gstin ? (validGSTIN(form.gstin) ? "valid" : "invalid") : null;

  const save = async () => {
    if (!form.name.trim()) { toast.error("Vendor name is required"); return; }
    if (form.gstin && !validGSTIN(form.gstin)) { toast.error("Vendor GST number is invalid"); return; }
    try {
      const payload = { ...form, address: [form.address.trim(), (form.address2 || "").trim()].filter(Boolean).join("\n") };
      delete payload.address2;
      if (editing) await api.put(`/vendors/${editing}`, payload);
      else await api.post("/vendors", payload);
      toast.success("Vendor saved");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const edit = (v) => {
    const [l1, ...rest] = (v.address || "").split("\n");
    setEditing(v.id);
    setForm({ ...empty, ...v, address: l1 || "", address2: rest.join(", ") });
    setOpen(true);
  };
  const del = async (id) => { if (!window.confirm("Delete vendor?")) return; await api.delete(`/vendors/${id}`); load(); };

  return (
    <div className="space-y-4" data-testid="vendors-page">
      <PageHeader title="Vendors" subtitle="Raw-material suppliers for purchase orders"
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
                      className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="v-new">
                <Plus size={14} className="mr-1" /> New Vendor
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-y-auto content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
              <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Vendor</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Company Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="v-name" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">GST Number</Label>
                  <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                         maxLength={15} className="mt-1.5 font-mono" data-testid="v-gstin" />
                  {gstStatus === "valid" && <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><CheckCircle2 size={12} /> Valid GSTIN</p>}
                  {gstStatus === "invalid" && <p className="text-xs text-rose-600 mt-1 flex items-center gap-1"><XCircle size={12} /> Invalid GSTIN</p>}
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Address Line 1</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" placeholder="Building / street / area" data-testid="v-address" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Address Line 2</Label>
                  <Input value={form.address2} onChange={(e) => setForm({ ...form, address2: e.target.value })} className="mt-1.5" placeholder="City / district / PIN" data-testid="v-address2" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">State</Label>
                  <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Credit Period</Label>
                  <Select value={String(form.credit_days ?? 60)} onValueChange={(v) => setForm({ ...form, credit_days: +v })}>
                    <SelectTrigger className="mt-1.5" data-testid="v-credit-days"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[15, 30, 45, 60, 90, 120].map((d) => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Contact Person</Label>
                  <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Email</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1.5" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="v-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Company</th>
              <th className="text-left px-4 py-2.5">GSTIN</th>
              <th className="text-left px-4 py-2.5 hidden md:table-cell">Contact</th>
              <th className="text-left px-4 py-2.5 hidden lg:table-cell">State</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pager.pageItems.map((v) => (
              <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`v-row-${v.id}`}>
                <td className="px-4 py-3 font-medium text-slate-800">{v.name}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-700">{v.gstin || "-"}</td>
                <td className="px-4 py-3 hidden md:table-cell text-slate-600">
                  <div>{v.contact_person}</div>
                  <div className="text-xs text-slate-500">{v.phone}</div>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell text-slate-600">{v.state || "-"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openLedger(v)} className="text-slate-500 hover:text-indigo-600 mr-2" title="Ledger & bills" data-testid={`v-ledger-${v.id}`}><BookOpen size={14} /></button>
                  <button onClick={() => edit(v)} className="text-slate-500 hover:text-blue-600 mr-2"><Pencil size={14} /></button>
                  <button onClick={() => del(v.id)} className="text-slate-500 hover:text-rose-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-sm text-slate-500">No vendors yet.</td></tr>}
          </tbody>
        </table>
        <Pager pager={pager} />
      </div>

      <Dialog open={!!ledger} onOpenChange={(v) => !v && setLedger(null)}>
        <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-y-auto content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3 pr-8">
              <span>Vendor Ledger — {ledgerVendor?.name}</span>
              <Button size="sm" variant="outline" onClick={openStatement} data-testid="vl-statement-pdf">
                <FileText size={13} className="mr-1.5" /> Statement PDF
              </Button>
            </DialogTitle>
          </DialogHeader>
          {ledger && (
            <>
              {excessPos.length > 0 && (
                <div className="border border-rose-300 bg-rose-50 text-rose-700 rounded-md px-4 py-2.5 text-sm font-medium flex items-start gap-2" data-testid="vl-excess-alert">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>Excess billing detected (net of debit notes): {excessPos.map((p) => `${p.number} — billed ${inr(billedByPo[p.number])} against PO value ${inr(p.total)}`).join(" · ")}</span>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="border border-slate-200 rounded-md p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Total PO Value</p>
                  <p className="text-lg font-bold tabular-nums" data-testid="vl-total-po">{inr(ledger.total_po)}</p>
                </div>
                <div className="border border-slate-200 rounded-md p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Total Billed</p>
                  <p className="text-lg font-bold tabular-nums text-emerald-700" data-testid="vl-total-billed">{inr(ledger.total_billed)}</p>
                </div>
                <div className="border border-slate-200 rounded-md p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Total Paid</p>
                  <p className="text-lg font-bold tabular-nums text-sky-700" data-testid="vl-total-paid">{inr(ledger.total_paid || 0)}</p>
                </div>
                <div className="border border-slate-200 rounded-md p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Outstanding Balance</p>
                  <p className={`text-lg font-bold tabular-nums ${(ledger.balance ?? (ledger.total_billed - (ledger.total_paid || 0))) > 0 ? "text-rose-600" : "text-slate-800"}`} data-testid="vl-balance">{inr(ledger.balance ?? (ledger.total_billed - (ledger.total_paid || 0)))}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start mt-2">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Purchase Orders</p>
                  <div className="border border-slate-200 rounded-md overflow-hidden">
                    <table className="w-full text-sm table-fixed" data-testid="vl-pos">
                      <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                        <tr className="h-9">
                          <th className="text-left px-3 py-2 w-[24%]">PO No.</th>
                          <th className="text-left px-3 py-2 w-[16%]">Date</th>
                          <th className="text-right px-3 py-2 w-[17%]">PO Value</th>
                          <th className="text-right px-3 py-2 w-[17%]">Billed</th>
                          <th className="text-left px-3 py-2 w-[26%]">Billing Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vlPoPager.pageItems.map((p) => {
                          const st = poStatus(p);
                          return (
                            <tr key={p.id} className={`border-t border-slate-100 h-[59px] ${(billedByPo[p.number] || 0) > (+p.total || 0) + 1 ? "bg-rose-50/60" : ""}`}>
                              <td className="px-3 py-2 font-mono text-xs truncate" title={p.number}>{p.number}<div className="text-[10px] text-slate-400 font-sans">{p.status}</div></td>
                              <td className="px-3 py-2">{fmtDate(p.date || p.created_at)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{inr(p.total)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{inr(billedByPo[p.number] || 0)}</td>
                              <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 whitespace-nowrap ${st.cls}`} data-testid={`vl-po-status-${p.id}`}>{st.label}</span></td>
                            </tr>
                          );
                        })}
                        {ledger.purchase_orders.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-sm text-slate-500">No purchase orders for this vendor.</td></tr>}
                      </tbody>
                    </table>
                    <Pager pager={vlPoPager} />
                  </div>

                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 mt-4">Payments Made</p>
                  <div className="border border-slate-200 rounded-md overflow-hidden">
                    <table className="w-full text-sm" data-testid="vl-payments">
                      <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                        <tr>
                          <th className="text-left px-3 py-2">Date</th>
                          <th className="text-left px-3 py-2">Mode / Ref</th>
                          <th className="text-left px-3 py-2">Cleared Bills / PO</th>
                          <th className="text-right px-3 py-2">Amount</th>
                          <th className="text-right px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(ledger.payments || []).map((p) => (
                          <tr key={p.id} className="border-t border-slate-100">
                            <td className="px-3 py-2">{fmtDate(p.date)}</td>
                            <td className="px-3 py-2 text-slate-600">{p.mode}{p.reference ? <span className="font-mono text-xs"> · {p.reference}</span> : ""}</td>
                            <td className="px-3 py-2 text-slate-600 font-mono text-xs">
                              {(p.allocations && p.allocations.length)
                                ? p.allocations.map((a) => `${a.bill_number || "bill"} (${inr(a.amount)})`).join(", ")
                                : ((p.bill_numbers && p.bill_numbers.length) ? p.bill_numbers.join(", ") : (p.po_number || "-"))}
                              {p.unallocated > 0 && <div className="text-[10px] text-amber-600 font-sans">+{inr(p.unallocated)} on account</div>}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-sky-700 font-medium">{inr(p.amount)}</td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {p.file_id && <button onClick={() => viewBillFile(p.file_id)} className="text-slate-400 hover:text-indigo-600 mr-1.5" title="View payment proof" data-testid={`vl-pay-file-${p.id}`}><Paperclip size={13} /></button>}
                              <button onClick={() => delPayment(p.id)} className="text-slate-400 hover:text-rose-600" data-testid={`vl-pay-del-${p.id}`}><Trash2 size={13} /></button>
                            </td>
                          </tr>
                        ))}
                        {!(ledger.payments || []).length && <tr><td colSpan={5} className="text-center py-6 text-sm text-slate-500">No payments recorded yet.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Vendor Bills (Invoices Received)</p>
                  <div className="border border-slate-200 rounded-md overflow-hidden">
                    <table className="w-full text-sm table-fixed" data-testid="vl-bills">
                      <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                        <tr className="h-9">
                          <th className="text-left px-3 py-2 w-[24%]">Bill No.</th>
                          <th className="text-left px-3 py-2 w-[16%]">Date</th>
                          <th className="text-left px-3 py-2 w-[27%]">Against PO</th>
                          <th className="text-right px-3 py-2 w-[17%]">Amount</th>
                          <th className="px-3 py-2 w-[16%]"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {vlBillPager.pageItems.map((b) => {
                          const isExcess = b.po_number && (billedByPo[b.po_number] || 0) > (+(ledger.purchase_orders.find((p) => p.number === b.po_number)?.total || 0)) + 1;
                          return (
                            <tr key={b.id} className={`border-t border-slate-100 h-[59px] ${isExcess ? "bg-rose-50/60" : ""}`}>
                              <td className="px-3 py-2 font-mono text-xs">
                                {b.is_cash ? (
                                  <span className="inline-flex items-center text-[10px] uppercase tracking-wider bg-amber-50 border border-amber-200 text-amber-700 rounded px-1.5 py-0.5 font-sans font-medium" data-testid={`vl-bill-cash-${b.id}`}>Cash · No Invoice</span>
                                ) : b.number}
                              </td>
                              <td className="px-3 py-2">{fmtDate(b.date)}</td>
                              <td className="px-3 py-2 text-slate-600 font-mono text-[11px] leading-tight whitespace-normal break-words" title={(b.allocations && b.allocations.length) ? b.allocations.map((a) => a.po_number).join(", ") : (b.po_number || "-")}>
                                {(b.allocations && b.allocations.length)
                                  ? b.allocations.map((a) => a.po_number).join(", ")
                                  : (b.po_number || "-")}
                              </td>
                              <td className={`px-3 py-2 text-right tabular-nums ${isExcess ? "text-rose-600 font-semibold" : ""}`}>
                                {inr(b.amount)}
                                {b.debit_note_number && <div className="text-[10px] text-orange-600 whitespace-nowrap">− DN {inr(b.debit_amount)} · <span className="font-mono">{b.debit_note_number}</span></div>}
                                {b.payment_status && (
                                  <div className={`text-[10px] uppercase tracking-wider mt-0.5 font-semibold whitespace-nowrap ${b.payment_status === "Paid" ? "text-emerald-600" : b.payment_status === "Partial" ? "text-amber-600" : "text-rose-500"}`} data-testid={`vl-bill-paystatus-${b.id}`}>
                                    {b.payment_status === "Paid" ? "Paid ✓" : b.payment_status === "Partial" ? `Part-paid · due ${inr(b.due_amount)}` : "Unpaid"}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                {b.debit_note_number
                                  ? <span className="inline-flex mr-1.5">
                                      <button onClick={() => viewDnPdf(b.id)} className="text-orange-600 hover:text-orange-800" title={`Debit Note ${b.debit_note_number} PDF`} data-testid={`vl-bill-dn-pdf-${b.id}`}><ReceiptText size={13} /></button>
                                      <button onClick={() => removeDn(b)} className="text-slate-300 hover:text-rose-600 ml-1" title={`Redo — remove Debit Note ${b.debit_note_number} and issue a fresh one`} data-testid={`vl-bill-dn-redo-${b.id}`}><RotateCcw size={12} /></button>
                                    </span>
                                  : <button onClick={() => { const al = (b.allocations || []).filter((a) => a.po_number); setDnFor({ bill: b, amount: al.length > 1 ? "" : (al.length === 1 ? al[0].amount : b.amount), po_number: al.length === 1 ? al[0].po_number : (al.length ? "" : b.po_number || ""), reason: "", action: "resupply" }); }} className="text-slate-400 hover:text-orange-600 mr-1.5" title="Material rejected — issue Debit Note" data-testid={`vl-bill-dn-${b.id}`}><PackageX size={13} /></button>}
                                {b.file_id ? (
                                  <button onClick={() => viewBillFile(b.file_id)} className="text-slate-400 hover:text-indigo-600 mr-1.5" title="View bill file" data-testid={`vl-bill-file-${b.id}`}><Paperclip size={13} /></button>
                                ) : (
                                  <label className={`inline-flex mr-1.5 cursor-pointer ${attachingBill === b.id ? "text-slate-300" : "text-amber-500 hover:text-amber-700"}`} title="No bill file yet — attach it (PDF / photo)" data-testid={`vl-bill-attach-${b.id}`}>
                                    <Upload size={13} />
                                    <input type="file" accept=".pdf,image/*" className="hidden" disabled={attachingBill === b.id}
                                           onChange={(e) => { attachBillFile(b, e.target.files?.[0]); e.target.value = ""; }} />
                                  </label>
                                )}
                                <button onClick={() => delBill(b.id)} className="text-slate-400 hover:text-rose-600" data-testid={`vl-bill-del-${b.id}`}><Trash2 size={13} /></button>
                              </td>
                            </tr>
                          );
                        })}
                        {ledger.bills.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-sm text-slate-500">No bills recorded yet.</td></tr>}
                      </tbody>
                    </table>
                    <Pager pager={vlBillPager} />
                  </div>

                  <div className="border border-slate-200 rounded-md p-3 mt-3">
                    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Add Vendor Bill</p>
                    <label className="flex items-center gap-2 mb-2 cursor-pointer select-none">
                      <input type="checkbox" checked={bill.is_cash}
                             onChange={(e) => setBill({ ...bill, is_cash: e.target.checked, number: e.target.checked ? "" : bill.number })}
                             className="h-3.5 w-3.5 accent-[#3B82F6]" data-testid="vl-bill-cash-check" />
                      <span className="text-xs text-slate-600">Cash purchase — no invoice received</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">{bill.is_cash ? "Bill No. (optional)" : "Bill No. *"}</Label>
                        <Input value={bill.number} onChange={(e) => setBill({ ...bill, number: e.target.value })} className="mt-1 h-9" placeholder={bill.is_cash ? "Cash purchase" : ""} data-testid="vl-bill-number" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Date</Label>
                        <DatePicker value={bill.date} onChange={(v) => setBill({ ...bill, date: v })} className="mt-1 h-9" testId="vl-bill-date" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Amount *</Label>
                        <Input type="number" value={bill.amount}
                               onChange={(e) => { setBill({ ...bill, amount: e.target.value }); if (club) setClubRows((rows) => resplit(rows, e.target.value)); }}
                               className="mt-1 h-9" data-testid="vl-bill-amount" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Against PO</Label>
                        {club ? (
                          <div className="mt-1 h-9 flex items-center text-xs font-medium text-blue-700" data-testid="vl-club-indicator">
                            Clubbed — {clubSel.length} PO{clubSel.length === 1 ? "" : "s"} selected below
                          </div>
                        ) : (
                          <Select value={bill.po_number || "none"} onValueChange={(v) => setBill({ ...bill, po_number: v === "none" ? "" : v })}>
                            <SelectTrigger className="mt-1 h-9" data-testid="vl-bill-po"><SelectValue placeholder="No PO" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No PO</SelectItem>
                              {ledger.purchase_orders.map((p) => (
                                <SelectItem key={p.id} value={p.number}>{p.number} · {inr(p.total)} (billed {inr(billedByPo[p.number] || 0)})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>
                    {!club && (
                      <button onClick={() => { setClub(true); setClubRows(resplit([{ po_number: bill.po_number || "", amount: "" }, { po_number: "", amount: "" }], bill.amount)); setBill({ ...bill, po_number: "" }); }}
                              className="text-[11px] font-medium text-blue-600 hover:underline mt-1.5" data-testid="vl-club-toggle">
                        + One bill for 2+ POs? Club purchase orders
                      </button>
                    )}
                    {club && (
                      <div className="mt-2 border border-blue-200 bg-blue-50/40 rounded-md p-2.5 space-y-1.5" data-testid="vl-club-box">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Club POs — split the bill amount</p>
                          <button onClick={resetClub} className="text-[11px] text-slate-500 hover:text-slate-800" data-testid="vl-club-off">Single PO</button>
                        </div>
                        {clubRows.map((r, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Select value={r.po_number || "none"} onValueChange={(v) => setClubPo(i, v === "none" ? "" : v)}>
                              <SelectTrigger className="h-8 flex-1 text-xs bg-white" data-testid={`vl-club-po-${i}`}><SelectValue placeholder="Select PO" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">—</SelectItem>
                                {ledger.purchase_orders
                                  .filter((p) => p.number === r.po_number || !clubRows.some((x) => x.po_number === p.number))
                                  .map((p) => (
                                    <SelectItem key={p.id} value={p.number}>{p.number} · {inr(p.total)} (billed {inr(billedByPo[p.number] || 0)})</SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            <Input type="number" value={r.amount} onChange={(e) => setClubAmt(i, e.target.value)}
                                   className="h-8 w-28 text-xs bg-white" placeholder="Amount" data-testid={`vl-club-amt-${i}`} />
                            {clubRows.length > 2 && (
                              <button onClick={() => removeClubRow(i)} className="text-slate-400 hover:text-rose-600" data-testid={`vl-club-del-${i}`}>
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        ))}
                        <div className="flex items-center justify-between pt-0.5">
                          <button onClick={() => setClubRows([...clubRows, { po_number: "", amount: "" }])}
                                  className="text-[11px] font-medium text-blue-600 hover:underline" data-testid="vl-club-add">+ Add another PO</button>
                          <span className={`text-[11px] font-medium ${clubOk ? "text-emerald-600" : "text-rose-600"}`} data-testid="vl-club-sum">
                            Split {inr(clubSum)} / Bill {inr(+bill.amount || 0)}{clubOk ? " ✓" : ` · diff ${inr(+( (+bill.amount || 0) - clubSum ).toFixed(2))}`}
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-500">Amounts auto-split by PO value — adjust manually if needed. The split must equal the bill amount; every linked PO gets stamped with this bill.</p>
                      </div>
                    )}
                    {selPo && liveExcess > 1 && (
                      <p className="text-xs text-rose-600 mt-2 flex items-center gap-1" data-testid="vl-live-excess">
                        <AlertTriangle size={12} /> This bill will exceed {selPo.number} ({inr(selPo.total)}) by {inr(liveExcess)}
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <span className="inline-flex items-center gap-1.5">
                        <label className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-blue-700 border border-dashed border-slate-300 rounded-md px-2.5 py-1.5 hover:border-blue-300 hover:bg-blue-50/50 cursor-pointer transition-colors duration-150" data-testid="vl-bill-file">
                          <Paperclip size={12} /> {billFile ? billFile.name : "Attach bill file (PDF / photo)"}
                          <input type="file" accept=".pdf,image/*" className="hidden"
                                 onChange={(e) => { setBillFile(e.target.files?.[0] || null); e.target.value = ""; }} />
                        </label>
                        {billFile && (
                          <button onClick={() => setBillFile(null)} className="text-slate-400 hover:text-rose-600 text-xs" title="Remove file" data-testid="vl-bill-file-remove">×</button>
                        )}
                      </span>
                      <Button size="sm" onClick={addBill} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="vl-bill-save"><Plus size={13} className="mr-1" /> Add Bill</Button>
                    </div>
                  </div>

                  <div className="border border-sky-200 bg-sky-50/40 rounded-md p-3 mt-3" data-testid="vl-aipay-card">
                    <div className="flex items-center justify-between">
                      <p className="text-xs uppercase tracking-widest text-sky-700 font-semibold flex items-center gap-1.5">
                        <Sparkles size={13} /> Payment Proof — AI Match
                      </p>
                      <label className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-md px-3 py-1.5 cursor-pointer transition-colors duration-150 ${aiPayBusy ? "bg-slate-200 text-slate-500" : "bg-sky-600 hover:bg-sky-700 text-white"}`} data-testid="vl-aipay-btn">
                        <Upload size={12} /> {aiPayBusy ? "Reading proof…" : "Upload Screenshot"}
                        <input type="file" accept=".pdf,image/*" className="hidden" onChange={uploadPayProof} disabled={aiPayBusy} data-testid="vl-aipay-input" />
                      </label>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">Upload the bank payment screenshot / UTR slip — AI reads the amount, date & reference and matches it to this vendor's bills.</p>
                    {aiPay && (
                      <div className="mt-3 space-y-2 border-t border-sky-100 pt-3" data-testid="vl-aipay-review">
                        {aiPay.summary && <p className="text-[11px] text-sky-700 italic">{aiPay.summary}</p>}
                        {aiPay.payee_name && !aiPay.payee_matches_vendor && (
                          <p className="text-[11px] text-rose-600 font-semibold flex items-center gap-1" data-testid="vl-aipay-payee-warn">
                            <AlertTriangle size={12} /> Payee on proof is "{aiPay.payee_name}" — doesn't look like this vendor. Double-check before recording.
                          </p>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Amount *</Label>
                            <Input type="number" value={aiPay.amount} onChange={(e) => setAiPay({ ...aiPay, amount: e.target.value })} className="mt-1 h-8 text-xs bg-white" data-testid="vl-aipay-amount" />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Date</Label>
                            <DatePicker value={aiPay.date} onChange={(v) => setAiPay({ ...aiPay, date: v })} className="mt-1 h-8" testId="vl-aipay-date" />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Mode</Label>
                            <Input value={aiPay.mode} onChange={(e) => setAiPay({ ...aiPay, mode: e.target.value })} className="mt-1 h-8 text-xs bg-white" data-testid="vl-aipay-mode" />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Reference / UTR</Label>
                            <Input value={aiPay.reference} onChange={(e) => setAiPay({ ...aiPay, reference: e.target.value })} className="mt-1 h-8 text-xs bg-white font-mono" data-testid="vl-aipay-ref" />
                          </div>
                        </div>
                        {(aiPay.matched_bills || []).length > 0 && (
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Matched Bills</Label>
                            <div className="flex flex-wrap gap-1.5 mt-1">
                              {aiPay.matched_bills.map((b, i) => (
                                <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-white border border-sky-200 text-sky-800 rounded px-2 py-1" data-testid={`vl-aipay-bill-${i}`}>
                                  <span className="font-mono">{b.bill_number}</span> · {inr(b.amount)}
                                  <button onClick={() => setAiPay({ ...aiPay, matched_bills: aiPay.matched_bills.filter((_, idx) => idx !== i) })} className="text-slate-400 hover:text-rose-600 ml-0.5">×</button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="flex justify-end gap-2 pt-1">
                          <Button size="sm" variant="outline" onClick={() => setAiPay(null)} data-testid="vl-aipay-cancel">Cancel</Button>
                          <Button size="sm" onClick={saveAiPay} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="vl-aipay-save"><CheckCircle2 size={13} className="mr-1" /> Confirm & Record Payment</Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="border border-slate-200 rounded-md p-3 mt-3">
                    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Add Vendor Payment</p>
                    <p className="text-[11px] text-slate-500 -mt-1 mb-2" data-testid="vl-pay-fifo-hint">Just enter the amount — it's automatically cleared against the <b>oldest unpaid bills first</b>; any excess stays on account.</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Amount *</Label>
                        <Input type="number" value={vpay.amount} onChange={(e) => setVpay({ ...vpay, amount: e.target.value })} className="mt-1 h-9" data-testid="vl-pay-amount" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Date</Label>
                        <DatePicker value={vpay.date} onChange={(v) => setVpay({ ...vpay, date: v })} className="mt-1 h-9" testId="vl-pay-date" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Mode</Label>
                        <Select value={vpay.mode} onValueChange={(v) => setVpay({ ...vpay, mode: v })}>
                          <SelectTrigger className="mt-1 h-9" data-testid="vl-pay-mode"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {["Bank Transfer", "UPI", "Cheque", "Cash", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Reference / UTR</Label>
                        <Input value={vpay.reference} onChange={(e) => setVpay({ ...vpay, reference: e.target.value })} className="mt-1 h-9" data-testid="vl-pay-ref" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Against Bills (optional — tick to add up)</Label>
                        {openBills.length === 0 ? (
                          <p className="text-[11px] text-slate-400 mt-1.5">No unpaid bills — the payment stays on account.</p>
                        ) : (
                          <div className="mt-1.5 max-h-36 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="vl-pay-bill-list">
                            {openBills.map((b) => (
                              <div key={b.id} onClick={() => togglePayBill(b)} className="flex items-start gap-2.5 px-2.5 py-2 cursor-pointer hover:bg-slate-50" data-testid={`vl-pay-bill-row-${b.number}`}>
                                <Checkbox checked={!!payBills[b.number]} className="mt-0.5 pointer-events-none shrink-0" data-testid={`vl-pay-bill-check-${b.number}`} />
                                <div className="flex-1">
                                  <div className="flex items-baseline justify-between gap-2">
                                    <div className="font-mono text-xs font-semibold break-all" style={{ color: "#0f172a" }}>{String(b.number || "—")}</div>
                                    <div className="tabular-nums text-rose-600 font-semibold text-[11px] whitespace-nowrap">due {inr(b.due_amount)}</div>
                                  </div>
                                  <div className="text-[11px] text-slate-500">{fmtDate(b.date)} · bill {inr(b.amount)}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {selPayBills.length > 0 && (
                          <p className="text-[11px] text-emerald-700 mt-1 font-medium" data-testid="vl-pay-bill-sum">
                            {selPayBills.length} bill{selPayBills.length === 1 ? "" : "s"} selected — amount set to their total due {inr(selPayBills.reduce((t, b) => t + (b.due_amount || 0), 0))}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-end mt-2">
                      <Button size="sm" onClick={addPayment} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="vl-pay-save"><Plus size={13} className="mr-1" /> Record Payment</Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!dnFor} onOpenChange={(v) => !v && setDnFor(null)}>
        <DialogContent className="max-w-md" data-testid="vl-dn-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PackageX size={15} className="text-orange-600" /> Material Rejected — Debit Note</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-1">
            Against bill <b className="font-mono">{dnFor?.bill?.number}</b> ({inr(dnFor?.bill?.amount || 0)}). The debit amount reduces what you owe this vendor.
          </p>
          <div className="space-y-3">
            {dnPos.length > 1 && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Against PO * (bill covers {dnPos.length} POs)</Label>
                <Select value={dnFor?.po_number || ""} onValueChange={(v) => {
                  const share = dnPos.find((a) => a.po_number === v)?.amount;
                  setDnFor({ ...dnFor, po_number: v, amount: share != null ? String(share) : dnFor.amount });
                }}>
                  <SelectTrigger className="mt-1 h-9" data-testid="vl-dn-po"><SelectValue placeholder="Select the PO the rejected material belongs to" /></SelectTrigger>
                  <SelectContent>
                    {dnPos.map((a) => (
                      <SelectItem key={a.po_number} value={a.po_number}>{a.po_number} · bill share {inr(a.amount)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Debit Amount (₹) *</Label>
                <Input type="number" value={dnFor?.amount ?? ""} onChange={(e) => setDnFor({ ...dnFor, amount: e.target.value })} className="mt-1 h-9" data-testid="vl-dn-amount" />
                {dnFor?.po_number && dnShare != null && (
                  Math.abs((+dnFor.amount || 0) - dnShare) <= 1
                    ? <p className="text-[10px] text-emerald-600 mt-1 font-medium" data-testid="vl-dn-match">✓ Matches {dnFor.po_number}'s share of the bill</p>
                    : (+dnFor.amount || 0) > dnShare + 1
                      ? <p className="text-[10px] text-rose-600 mt-1 font-medium" data-testid="vl-dn-exceeds">Exceeds this PO's bill share ({inr(dnShare)}) — will be rejected</p>
                      : <p className="text-[10px] text-amber-600 mt-1 font-medium" data-testid="vl-dn-partial">Partial debit — this PO's bill share is {inr(dnShare)}</p>
                )}
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Debit Note No</Label>
                <Input value={dnFor?.dn_number ?? ""} onChange={(e) => setDnFor({ ...dnFor, dn_number: e.target.value })} placeholder="Auto (next in sequence)" className="mt-1 h-9" data-testid="vl-dn-number" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Debit Note Date</Label>
                <Input type="date" value={dnFor?.date ?? ""} onChange={(e) => setDnFor({ ...dnFor, date: e.target.value })} className="mt-1 h-9" data-testid="vl-dn-date" />
                <p className="text-[10px] text-slate-400 mt-1">Leave empty for today — set it to the return date (e.g. same day as the credit note)</p>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Reason</Label>
              <Input value={dnFor?.reason ?? ""} onChange={(e) => setDnFor({ ...dnFor, reason: e.target.value })} placeholder="e.g. 40 Mtr silicone tube failed QC — hardness out of spec" className="mt-1 h-9" data-testid="vl-dn-reason" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">What happens to the order?</Label>
              <div className="space-y-1.5 mt-1.5">
                {[["resupply", "Re-supply & Re-dispatch", "Vendor replaces the material — PO re-opens, linked order goes back to In Production"],
                  ["cancel", "Cancel Order — Refund", "PO and linked sales order are cancelled; refund the customer from Payments → Refund"],
                  ["", "Financial adjustment only", "Just the debit note — PO and order statuses stay unchanged"]].map(([v, l, hint]) => (
                  <label key={v || "none"} className={`flex items-start gap-2 border rounded-md px-3 py-2 cursor-pointer transition-colors duration-150 ${(dnFor?.action || "") === v ? "border-orange-400 bg-orange-50/50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`vl-dn-action-${v || "none"}`}>
                    <input type="radio" name="dn-action" checked={(dnFor?.action || "") === v} onChange={() => setDnFor({ ...dnFor, action: v })} className="mt-0.5 accent-orange-600" />
                    <span>
                      <span className="text-xs font-semibold text-slate-800">{l}</span>
                      <span className="block text-[10px] text-slate-500">{hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDnFor(null)}>Cancel</Button>
              <Button size="sm" onClick={issueDn} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="vl-dn-issue">Issue Debit Note</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
